// Hulpfuncties voor het prestatiescherm (contractlijnen, uren)

export const COMPENSATION_LABELS = {
  hourly: "Per uur",
  per_item: "Per handeling",
  salaried: "Intern (urenstaat)",
};

export const WEEKDAYS = [
  { key: "mon", label: "Maandag", short: "ma" },
  { key: "tue", label: "Dinsdag", short: "di" },
  { key: "wed", label: "Woensdag", short: "wo" },
  { key: "thu", label: "Donderdag", short: "do" },
  { key: "fri", label: "Vrijdag", short: "vr" },
  { key: "sat", label: "Zaterdag", short: "za" },
  { key: "sun", label: "Zondag", short: "zo" },
];

// "9:00", "9.30", "9,5", "930" of "0900" -> minuten; leeg -> 0; ongeldig -> null
export function parseDurationToMinutes(text) {
  const value = String(text ?? "").trim();
  if (value === "") return 0;

  let match = value.match(/^(\d{1,2})[:h](\d{1,2})$/i);
  if (match) {
    const [h, m] = [Number(match[1]), Number(match[2])];
    return m < 60 ? h * 60 + m : null;
  }
  match = value.match(/^(\d{1,2})[.,](\d{1,2})$/);
  if (match) {
    const hours = Number(`${match[1]}.${match[2]}`);
    return Math.round(hours * 60);
  }
  match = value.match(/^(\d{1,2})$/);
  if (match) return Number(match[1]) * 60;
  match = value.match(/^(\d{1,2})(\d{2})$/);
  if (match) {
    const [h, m] = [Number(match[1]), Number(match[2])];
    return m < 60 ? h * 60 + m : null;
  }
  return null;
}

// 540 -> "9:00"; 0 -> ""
export function formatMinutes(minutes) {
  if (!minutes) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

// "2026-12-31" -> "31/12/2026"
export function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function todayIso() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

// "upcoming" | "active" | "ended"
export function lineStatus(line, today = todayIso()) {
  if (line.start_date > today) return "upcoming";
  if (line.end_date && line.end_date < today) return "ended";
  return "active";
}

export function emptySchedule(cycleWeeks = 1, anchorDate = "") {
  const week = () => Object.fromEntries(WEEKDAYS.map((d) => [d.key, 0]));
  const weeks = Array.from({ length: cycleWeeks }, week);
  const schedule = { cycle_weeks: cycleWeeks, weeks };
  if (cycleWeeks === 2) schedule.anchor_date = anchorDate;
  return schedule;
}

export function percentageChange(previousRate, newRate) {
  const previous = parseFloat(previousRate);
  const next = parseFloat(newRate);
  if (!previous || Number.isNaN(next)) return null;
  return Math.round(((next - previous) / previous) * 1000) / 10;
}

export function formatEuro(value) {
  if (value === null || value === undefined || value === "") return "";
  return `€ ${Number(value).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Tijdstippen (uren van de dag) voor de invoer van prestaties
// ---------------------------------------------------------------------------

// "9", "9:30", "930", "0930", "9.30", "9h30", "9u30" -> minuten sinds middernacht; ongeldig -> null
export function parseTimeOfDay(text) {
  const value = String(text ?? "").trim().toLowerCase();
  if (value === "") return null;
  let hours;
  let minutes = 0;
  let match = value.match(/^(\d{1,2})[:.hu](\d{1,2})$/);
  if (match) {
    hours = Number(match[1]);
    minutes = Number(match[2]);
  } else if ((match = value.match(/^(\d{1,2})$/))) {
    hours = Number(match[1]);
  } else if ((match = value.match(/^(\d{1,2})(\d{2})$/))) {
    hours = Number(match[1]);
    minutes = Number(match[2]);
  } else {
    return null;
  }
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

// 570 -> "09:30"; null/"" -> ""
export function formatTimeOfDay(minutes) {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Pijltjes: 5 minuten, Shift 15 minuten, Ctrl 1 uur. Binnen 00:00-23:59.
export function stepTimeOfDay(minutes, direction, { shift = false, ctrl = false } = {}) {
  const step = ctrl ? 60 : shift ? 15 : 5;
  const base = minutes === null || minutes === undefined ? 9 * 60 : minutes;
  // vanaf een ongeregeld tijdstip eerst naar het volgende veelvoud van de stap
  const aligned = direction > 0 ? Math.floor(base / step) * step + step : Math.ceil(base / step) * step - step;
  return Math.min(1439, Math.max(0, base % step === 0 ? base + direction * step : aligned));
}

// "08:00:00" of "08:00" -> "08:00"
export function trimSeconds(time) {
  return time ? String(time).slice(0, 5) : "";
}

// Gewerkte minuten: einde - start - pauze (lege pauze = geen aftrek); null bij ongeldige combinatie
export function workedMinutes(start, end, breakMinutes) {
  const s = parseTimeOfDay(start);
  const e = parseTimeOfDay(end);
  if (s === null || e === null || e <= s) return null;
  const total = e - s - (Number(breakMinutes) || 0);
  return total > 0 ? total : null;
}

// Voorgestelde pauze voor een lijn (zelfde regel als de server): enkel met voorinvullen en vanaf de drempel
export function defaultBreak(line, start, end) {
  if (!line || !line.break_prefill) return "";
  const s = parseTimeOfDay(start);
  const e = parseTimeOfDay(end);
  if (s === null || e === null || e <= s) return "";
  return e - s >= Number(line.break_threshold_hours) * 60 ? String(line.break_minutes) : "";
}

// 450 -> "7u30"; 480 -> "8u00"
export function formatHoursMinutes(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}u${String(m).padStart(2, "0")}`;
}

export function weekdayName(iso) {
  const names = ["Zondag", "Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag"];
  return names[new Date(`${iso}T00:00:00`).getDay()];
}

export const MONTH_NAMES = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

export const PERIOD_STATUS = {
  open: { label: "Open", className: "bg-gray-100 text-gray-700" },
  submitted: { label: "Ingediend", className: "bg-blue-100 text-blue-800" },
  release_requested: { label: "Vrijgave gevraagd", className: "bg-amber-100 text-amber-800" },
  approved: { label: "Goedgekeurd", className: "bg-green-100 text-green-800" },
};

export const ABSENCE_LABELS = { absent: "Afwezig", sick: "Ziek" };

// "API-fout 400: {"detail":"Tekst"}" -> "Tekst"
export function apiErrorMessage(err, fallback = "Er ging iets mis.") {
  const raw = (err && err.message) || "";
  const start = raw.indexOf("{");
  if (start >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(start));
      if (typeof parsed.detail === "string") return parsed.detail;
    } catch (e) {
      // geen JSON: val terug op de ruwe tekst
    }
  }
  return raw || fallback;
}

// Minuten met teken: 60 -> "+1u00", -45 -> "-0u45"; nul -> "0u00" (of "" zonder alwaysShowZero)
export function formatSignedMinutes(minutes, alwaysShowZero = false, plus = true) {
  if (!minutes) return alwaysShowZero ? "0u00" : "";
  const sign = minutes < 0 ? "-" : plus ? "+" : "";
  const abs = Math.abs(minutes);
  return `${sign}${Math.floor(abs / 60)}u${String(abs % 60).padStart(2, "0")}`;
}

// ISO-weeknummer als sleutel ("2026-41"), om dagen per week te groeperen
export function isoWeekKey(iso) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + 3); // donderdag van die week
  const firstThursday = new Date(d.getFullYear(), 0, 4);
  const week = 1 + Math.round(((d - firstThursday) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-${week}`;
}

// "-4:55", "105:14", "+1,5", "8" -> minuten met teken (saldi kunnen boven 99 uur komen); leeg -> 0; ongeldig -> null
export function parseSignedDuration(text) {
  const value = String(text ?? "").trim();
  if (value === "") return 0;
  const negative = value.startsWith("-");
  const body = value.replace(/^[-+]/, "");
  let minutes = null;
  let match = body.match(/^(\d{1,4})[:h](\d{1,2})$/i);
  if (match) {
    minutes = Number(match[2]) < 60 ? Number(match[1]) * 60 + Number(match[2]) : null;
  } else if ((match = body.match(/^(\d{1,4})[.,](\d{1,2})$/))) {
    minutes = Math.round(Number(`${match[1]}.${match[2]}`) * 60);
  } else if ((match = body.match(/^(\d{1,4})$/))) {
    minutes = Number(match[1]) * 60;
  }
  if (minutes === null) return null;
  return negative ? -minutes : minutes;
}

// ---------------------------------------------------------------------------
// Jaaroverzicht
// ---------------------------------------------------------------------------

// Zachte achtergrondkleuren in het jaarraster: uren groen, afwezigheidscodes geel, rood of oranje
export const TONE_CLASSES = {
  green: "bg-green-100 text-green-900",
  yellow: "bg-yellow-100 text-yellow-900",
  red: "bg-red-100 text-red-800",
  orange: "bg-orange-100 text-orange-800",
};

// Dezelfde tinten als hexkleur (voor de diagonale deling van een cel met uren én een code)
export const TONE_COLORS = { green: "#dcfce7", yellow: "#fef9c3", red: "#fee2e2", orange: "#ffedd5" };

// 230/235 (verlof, vakantie) geel, 250 (ziek) rood, alle andere codes (feestdag, klein verlet, onbetaald, ...) oranje
export function codeTone(code) {
  if (code === "230" || code === "235") return "yellow";
  if (code === "250") return "red";
  return "orange";
}

/**
 * Zet de registraties van een jaar om naar een raster: per maand een totaal en per dag een cel.
 * Cel: { minutes, cost, items[], kind } waarbij kind bepaalt wat getoond wordt
 * ("hours" | "items" | "absent" | "sick" | "code").
 */
export function buildYearGrid(entries, statuses = {}) {
  const months = Array.from({ length: 12 }, (_, i) => ({
    month: i + 1, status: statuses[String(i + 1)] || "open", days: {}, minutes: 0, cost: 0, hasCost: false,
  }));
  entries.forEach((e) => {
    const month = months[Number(e.entry_date.slice(5, 7)) - 1];
    const day = Number(e.entry_date.slice(8, 10));
    const cell = month.days[day] || { minutes: 0, cost: 0, items: [], kinds: new Set(), codes: [] };
    cell.items.push(e);
    if (e.entry_type === "absence") cell.kinds.add(e.absence_type === "sick" ? "sick" : "absent");
    else if (e.compensation_type === "per_item") cell.kinds.add("items");
    else if (e.entry_type === "code" && e.code !== "100") {
      cell.kinds.add("code");
      if (!cell.codes.includes(e.code)) cell.codes.push(e.code);
    }
    cell.minutes += e.minutes || 0;
    if (e.cost !== null && e.cost !== undefined) {
      cell.cost += Number(e.cost);
      month.cost += Number(e.cost);
      month.hasCost = true;
    }
    month.minutes += e.minutes || 0;
    month.days[day] = cell;
  });
  months.forEach((m) =>
    Object.values(m.days).forEach((cell) => {
      cell.kind = cell.minutes > 0 ? "hours" : cell.kinds.has("items") ? "items" : cell.kinds.has("sick") ? "sick"
        : cell.kinds.has("absent") ? "absent" : "code";
      cell.tone = cell.kind === "hours" || cell.kind === "items" ? "green" : cell.kind === "sick" ? "red"
        : cell.kind === "absent" ? "orange" : codeTone(cell.codes[0]);
      // gewerkte uren én een afwezigheid op dezelfde dag (bv. een halve dag): de cel wordt diagonaal in twee gedeeld
      if (cell.minutes > 0) {
        const secondary = cell.kinds.has("sick") ? { text: "Z", tone: "red" }
          : cell.kinds.has("absent") ? { text: "A", tone: "orange" }
          : cell.codes.length ? { text: cell.codes.join(","), tone: codeTone(cell.codes[0]) } : null;
        if (secondary) cell.secondary = secondary;
      }
    })
  );
  const total = months.reduce((acc, m) => ({ minutes: acc.minutes + m.minutes, cost: acc.cost + m.cost }), { minutes: 0, cost: 0 });
  const approved = months.filter((m) => m.status === "approved").reduce((acc, m) => ({ minutes: acc.minutes + m.minutes, cost: acc.cost + m.cost }), { minutes: 0, cost: 0 });
  return { months, total, approved, hasCost: months.some((m) => m.hasCost) };
}

// ---------------------------------------------------------------------------
// Groepering per omschrijving (zelfde regels als de PDF in de backend)
// ---------------------------------------------------------------------------

export const ABSENCE_GROUP_LABEL = "Afwezigheid";

/**
 * Registraties groeperen per omschrijving van de contractlijn, met som van minuten, aantal en kost.
 * Werkgroepen staan alfabetisch, afwezigheden als laatste groep; binnen een groep op datum en start.
 */
export function groupEntriesByDescription(entries) {
  const groups = new Map();
  entries.forEach((e) => {
    const absence = e.entry_type === "absence";
    const label = absence ? ABSENCE_GROUP_LABEL : (e.line_description || "").trim() || "Zonder omschrijving";
    const key = absence ? "\uffff" : label.toLowerCase();
    if (!groups.has(key)) groups.set(key, { key, label, kind: absence ? "absence" : "work", items: [], minutes: 0, quantity: 0, cost: 0 });
    const group = groups.get(key);
    group.items.push(e);
    group.minutes += e.minutes || 0;
    group.quantity += e.quantity ? Number(e.quantity) : 0;
    group.cost += e.cost ? Number(e.cost) : 0;
  });
  groups.forEach((g) =>
    g.items.sort((a, b) => (a.entry_date + (a.start_time || "")).localeCompare(b.entry_date + (b.start_time || "")))
  );
  return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

export function sumEntries(entries) {
  return entries.reduce(
    (acc, e) => ({ minutes: acc.minutes + (e.minutes || 0), cost: acc.cost + (e.cost ? Number(e.cost) : 0) }),
    { minutes: 0, cost: 0 }
  );
}
